import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { placeListRecoil } from '@/atom/PlaceAtom';
import { entryListRecoil, entryListLengthRecoil } from '@/atom/EntryAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { ApiService } from '@/api';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import { EntryDto, EntrySearchDto } from '@/model/entry.dto';
import { PlaceDto } from '@/model/place.dto';
import { ApexOptions } from 'apexcharts';
import ApexCharts from 'react-apexcharts';
import {
  Map,
  MapMarker,
  MapTypeControl,
  ZoomControl,
} from 'react-kakao-maps-sdk';
import useKakaoLoader from '@/utils/useKakaoLoader';

declare global {
  interface Window {
    kakao: any;
  }
}

const Dashboard = () => {
  const api = useMemo(() => new ApiService(), []);
  const navigate = useNavigate();

  const [entryListState, setEntryListState] = useRecoilState(entryListRecoil);
  const [entryListLengthState, setEntryListLengthState] = useRecoilState(
    entryListLengthRecoil,
  );
  const [placeListState, setPlaceListState] = useRecoilState(placeListRecoil);

  const [tomorrowDate, setTomorrowDate] = useState('');
  const [todayDate, setTodayDate] = useState('');
  const [todayDateTransform, setTodayDateTransform] = useState('');
  const [yesterdayDate, setYesterdayDate] = useState('');
  const [beforeYesterdayDate, setBeforeYesterdayDate] = useState('');
  const [graphTodayDate, setGraphTodayDate] = useState('');
  const [graphYesterdayDate, setGraphYesterdayDate] = useState('');
  const [graphBeforeYesterdayDate, setGraphBeforeYesterdayDate] = useState('');
  const [graphTodayEntryNum, setGraphTodayEntryNum] = useState<number>(0);
  const [graphYesterdayEntryNum, setGraphYesterdayEntryNum] =
    useState<number>(0);
  const [graphBeforeYesterdayEntryNum, setGraphBeforeYesterdayEntryNum] =
    useState<number>(0);
  const [selectPlace, setSelectPlace] = useState<PlaceDto>();
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(3);
  const [placeIndex, setPlaceIndex] = useState<number>(0);
  const [placeListLoaded, setPlaceListLoaded] = useState<boolean>(false);
  const [center, setCenter] = useState({ lat: 33.450701, lng: 126.570667 });

  const lineSerise = [
    {
      name: 'value',
      data: [
        graphBeforeYesterdayEntryNum,
        graphYesterdayEntryNum,
        graphTodayEntryNum,
      ],
    },
  ];
  const lineOptions: ApexOptions = {
    chart: {
      id: 'model-setup-chart',
      height: '18.4rem',
      width: '18.4rem',
      toolbar: {
        show: false,
      },
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'linear',
        speed: 1200,
        dynamicAnimation: {
          speed: 1000,
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value) => `${value}`,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,
        opacityTo: 0.8,
        stops: [0, 80, 100],
      },
    },
    colors: ['#4880FF'],
    xaxis: {
      axisBorder: { show: true },
      axisTicks: { show: false },
      labels: {
        show: true,
        style: {
          fontFamily: '"SpoqaHanSansNeo"',
          fontSize: '15px',
          fontWeight: '400',
        },
      },
      offsetX: -10,
      categories: [
        graphBeforeYesterdayDate,
        graphYesterdayDate,
        graphTodayDate,
      ],
    },
    grid: { show: false },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    yaxis: {
      show: true,
      axisBorder: { show: true },
      tickAmount: 2,
      min: 0,
      forceNiceScale: true,
      labels: {
        style: {
          fontFamily: '"SpoqaHanSansNeo"',
          fontSize: '13px',
          fontWeight: '300',
          colors: ['#999'],
        },
        offsetX: -10,
      },
    },
  };

  const getFormattedDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const geocodeAddress = (address: string) => {
    const kakao = window.kakao;
    if (!kakao || !kakao.maps) {
      console.error('Kakao Maps API가 로드되지 않았습니다.');
      return;
    }
    const geocoder = new kakao.maps.services.Geocoder();
    geocoder.addressSearch(address, (result: any, status: any) => {
      if (status === kakao.maps.services.Status.OK) {
        const newCenter = {
          lat: parseFloat(result[0].y),
          lng: parseFloat(result[0].x),
        };
        setCenter(newCenter);
      } else {
        console.error('주소 검색에 실패했습니다.');
      }
    });
  };

  const fetchBeforeYesterdayEntryNum = async () => {
    try {
      const dto: EntrySearchDto = {
        start_date: beforeYesterdayDate,
        end_date: yesterdayDate,
        place_id: selectPlace?.place_id,
      };
      const response: EntryDto[] = await api.getEntryNum(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      if (response.length === 0) {
        setGraphBeforeYesterdayEntryNum(0);
      } else {
        setGraphBeforeYesterdayEntryNum(Number(response[0].count));
      }
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const fetchYesterdayEntryNum = async () => {
    try {
      const dto: EntrySearchDto = {
        start_date: yesterdayDate,
        end_date: todayDate,
        place_id: selectPlace?.place_id,
      };
      const response: EntryDto[] = await api.getEntryNum(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      if (response.length === 0) {
        setGraphYesterdayEntryNum(0);
      } else {
        setGraphYesterdayEntryNum(Number(response[0].count));
      }
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const fetchTodayEntryNum = async () => {
    try {
      const dto: EntrySearchDto = {
        start_date: todayDate,
        end_date: tomorrowDate,
        place_id: selectPlace?.place_id,
      };
      const response: EntryDto[] = await api.getEntryNum(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      if (response.length === 0) {
        setGraphTodayEntryNum(0);
      } else {
        setGraphTodayEntryNum(Number(response[0].count));
      }
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const fetchEntryDailyList = async () => {
    try {
      const dto: EntrySearchDto = {
        start_date: todayDate,
        end_date: todayDate,
        place_id: selectPlace?.place_id,
      };
      const response: EntryDto[] = await api.getEntryList(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      setEntryListLengthState(response.length);
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const filterEntryDailyList = async () => {
    const dto: EntrySearchDto = {
      start_date: todayDate,
      end_date: todayDate,
      place_id: selectPlace?.place_id,
      offset,
      limit,
    };
    try {
      const response: EntryDto[] = await api.getEntryList(
        String(sessionStorage.getItem('accessToken')),
        dto,
      );
      setEntryListState(response);
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const fetchPlaceList = async () => {
    try {
      const response: PlaceDto[] = await api.getPlaceList(
        String(sessionStorage.getItem('accessToken')),
      );
      setPlaceListState(response.filter((place) => place.is_active === 1));
    } catch (err: any) {
      if (err.response.data.statusCode === 401) {
        sessionStorage.clear();
        navigate('/login');
      }
    }
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
  };

  const handlePlaceIndexChange = (place: PlaceDto, index: number) => {
    setPlaceIndex(index);
    setSelectPlace(place);
    geocodeAddress(`${place.address} ${place.detail}`);
  };

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    setTomorrowDate(getFormattedDate(tomorrow));
    setTodayDate(getFormattedDate(today));

    const formatted = new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'long',
    }).format(today);
    const [year, month, day, weekday] = formatted.split('. ');
    const finalDate = `${year}. ${month}. ${day} ${weekday}`;
    setTodayDateTransform(finalDate);

    const graphToday = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
    }).format(today);
    setGraphTodayDate(graphToday);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const graphYesterday = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
    }).format(yesterday);
    setGraphYesterdayDate(graphYesterday);
    setYesterdayDate(getFormattedDate(yesterday));

    const beforeYesterday = new Date(today);
    beforeYesterday.setDate(today.getDate() - 2);
    const graphBeforeYesterday = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
    }).format(beforeYesterday);
    setGraphBeforeYesterdayDate(graphBeforeYesterday);
    setBeforeYesterdayDate(getFormattedDate(beforeYesterday));
  }, []);

  useEffect(() => {
    if (todayDate) {
      fetchBeforeYesterdayEntryNum();
      fetchYesterdayEntryNum();
      fetchTodayEntryNum();
      fetchEntryDailyList();
      filterEntryDailyList();
      fetchPlaceList().then(() => {
        setPlaceListLoaded(true);
      });
    }
  }, [
    selectPlace,
    offset,
    todayDate,
    graphTodayDate,
    graphYesterdayDate,
    graphBeforeYesterdayDate,
  ]);

  useEffect(() => {
    if (placeListState !== null && placeListState.length !== 0) {
      const beginningSelectPlace = placeListState[0];
      setSelectPlace(beginningSelectPlace);
      geocodeAddress(
        `${beginningSelectPlace.address} ${beginningSelectPlace.detail}`,
      );
    }
  }, [placeListLoaded]);

  return (
    <>
      <Wrap className="flex">
        <LeftBox className="flex fdc">
          <CntBox1 className="flex fdc">
            <TitleTextBox text="금일 출입 현황" />
            <ul className="flex">
              <li className="flex fdc jcsb">
                <h3 className="tm">날짜</h3>
                <p className="tb tar">{todayDateTransform}</p>
              </li>
              <li className="flex fdc jcsb">
                <h3 className="tm">출입 인원 수</h3>
                {placeListState?.length === 0 ? (
                  <p className="tb tar">0</p>
                ) : (
                  <p className="tb tar">{entryListLengthState}</p>
                )}
              </li>
            </ul>
          </CntBox1>
          <CntBox2 className="flex fdc">
            <TitleTextBox text="일일 출입 건수" />
            {placeListState?.length === 0 ? (
              <NoDataText>등록 혹은 설정된 장소가 없습니다.</NoDataText>
            ) : (
              <ApexCharts
                type="area"
                height={'150'}
                series={lineSerise}
                options={lineOptions}
              />
            )}
          </CntBox2>
          <CntBox3 className="flex fdc">
            <TitleTextBox text="금일 출입 인원 정보" />
            {entryListLengthState !== 0 && placeListState?.length !== 0 && (
              <CS.TableType1Mini>
                <table>
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>휴대폰 번호</th>
                      <th>출입자 번호</th>
                      <th>시각</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryListState?.map((list, index) => (
                      <tr key={index}>
                        <td>{list.applicant}</td>
                        <td>{list.phone}</td>
                        <td>{list.certi_id}</td>
                        <td>{list.entry_time?.split(' ')[1].slice(0, 8)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1Mini>
            )}
            {entryListLengthState !== 0 && placeListState?.length !== 0 && (
              <Pagenation
                listAllLength={entryListLengthState}
                listLimit={limit}
                onCurrentPageChange={handleOffsetChange}
                miniSize={true}
              />
            )}
            {placeListState?.length === 0 && (
              <NoDataText>등록 혹은 설정된 장소가 없습니다.</NoDataText>
            )}
            {entryListLengthState === 0 && (
              <NoDataText>금일 출입 인원이 없습니다.</NoDataText>
            )}
          </CntBox3>
        </LeftBox>
        <RightBox className="flex fdc">
          <CntBox4 className="flex fdc">
            {placeListState?.length !== 0 && (
              <CS.TabMenuType1>
                {placeListState?.map(
                  (place, index) =>
                    place.is_active === 1 && (
                      <button
                        key={place.place_id}
                        className={placeIndex === index ? 'active' : ''}
                        onClick={() => handlePlaceIndexChange(place, index)}
                      >
                        {place.name}
                      </button>
                    ),
                )}
              </CS.TabMenuType1>
            )}
            {placeListState?.length !== 0 && (
              <TableBox>
                <table>
                  <tbody>
                    <tr>
                      <th>출입장소</th>
                      <td>{selectPlace?.name}</td>
                      <th>금일 출입 인원</th>
                      <td>{entryListLengthState}</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>주소</th>
                      <td rowSpan={2}>
                        {selectPlace?.address} {selectPlace?.detail}
                      </td>
                      <th>관리자명</th>
                      <td>{selectPlace?.manager}</td>
                    </tr>
                    <tr>
                      <th>관리자 연락처</th>
                      <td>{selectPlace?.phone}</td>
                    </tr>
                  </tbody>
                </table>
              </TableBox>
            )}
            {placeListState?.length !== 0 && (
              <MapBox>
                <Map
                  center={center}
                  style={{ width: '100%', height: '100%' }}
                  level={3}
                  draggable={true}
                  zoomable={true}
                >
                  <MapMarker position={center}></MapMarker>
                </Map>
              </MapBox>
            )}
            {placeListState?.length === 0 && (
              <NoDataText>등록 혹은 설정된 장소가 없습니다.</NoDataText>
            )}
          </CntBox4>
        </RightBox>
      </Wrap>
    </>
  );
};

export default Dashboard;

export const Wrap = styled.div`
  width: 100%;
  height: 100%;
  gap: 20px;
  @media (max-width: 1420px) {
    gap: 12px;
  }
`;

export const LeftBox = styled.div`
  width: 33.227%;
  gap: 20px;
  > ${CS.CntBoxType1} {
    gap: 12px;
  }
  @media (max-width: 1420px) {
    width: 33.227%;
    gap: 12px;
    > ${CS.CntBoxType1} {
      gap: 8px;
    }
  }
`;

export const RightBox = styled.div`
  width: 66.773%;
  > ${CS.CntBoxType1} {
    flex: 1 0 0%;
  }
`;

export const CntBox1 = styled(CS.CntBoxType1)`
  flex: 1 0 0%;
  > ul {
    gap: 12px;
    height: 100%;
    > li {
      height: 100%;
      background: #f9f9f9;
      border-radius: 12px;
      box-sizing: border-box;
      padding: 16px;
      &:first-child {
        flex-grow: 2.5;
        > p {
          color: #4880ff;
        }
      }
      &:last-child {
        flex-grow: 1;
      }
      > h3 {
        font-size: 16px;
        color: #333;
      }
      > p {
        font-size: 20px;
        color: #222;
      }
    }
  }
  @media (max-width: 1420px) {
    > ul {
      gap: 8px;
      > li {
        padding: 12px;
        > h3 {
          font-size: 14px;
        }
        > p {
          font-size: 18px;
        }
      }
    }
  }
`;

export const CntBox2 = styled(CS.CntBoxType1)`
  flex: 1 0 0%;
  padding: 24px 20px 5px 20px;
  position: relative;
`;

export const CntBox3 = styled(CS.CntBoxType1)`
  flex: 1.5 0 0%;
  position: relative;
  > p {
    font-size: 16px;
    color: #222;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  @media (max-width: 1420px) {
    > p {
      font-size: 14px;
    }
  }
`;

export const CntBox4 = styled(CS.CntBoxType1)`
  position: relative;
  > p {
    font-size: 16px;
    color: #222;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  @media (max-width: 1420px) {
    > p {
      font-size: 14px;
    }
  }
`;

export const TableBox = styled(CS.TableType1_2)`
  width: 100%;
  min-height: 151.5px;
  max-height: 151.5px;
  margin-top: 16px;
  margin-bottom: 16px;
  @media (max-width: 1420px) {
    min-height: 127.5px;
    max-height: 127.5px;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

export const MapBox = styled.div`
  width: 100%;
  height: 100%;
`;

export const NoDataText = styled.p`
  font-size: 16px;
  white-space: nowrap;
  color: #222;
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media (max-width: 1420px) {
    font-size: 14px;
  }
`;
